// src/components/EditExerciseDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { updateExercise } from '../services/exerciseService';

const EditExerciseDialog = ({ open, exercise, onClose, onSuccess, onNotify }) => {
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    status: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (exercise) {
      setFormData({
        name: exercise.name,
        startDate: exercise.startDate?.split('T')[0] || '',
        status: exercise.status,
      });
    }
  }, [exercise]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await updateExercise(exercise.id, {
        name: formData.name,
        startDate: formData.startDate,
        status: formData.status,
      });
      if (onNotify) onNotify(`Exercise "${formData.name}" updated successfully`, 'success');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update exercise';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  // Build status options, including the current (possibly legacy) value so it
  // always shows correctly even for older exercises created as PLANNED.
  const baseStatuses = ['DRAFT', 'ACTIVE', 'CLOSED'];
  const statusOptions = baseStatuses.includes(formData.status)
    ? baseStatuses
    : [formData.status, ...baseStatuses].filter(Boolean);

  const statusLabel = (s) => {
    switch (s) {
      case 'DRAFT': return 'Draft';
      case 'ACTIVE': return 'Active';
      case 'CLOSED': return 'Closed';
      case 'PLANNED': return 'Planned (legacy)';
      case 'COMPLETED': return 'Completed (legacy)';
      default: return s;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Exercise</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Year"
            value={exercise?.year || ''}
            disabled
            margin="normal"
            helperText="Year cannot be changed"
          />

          <TextField
            fullWidth
            label="Exercise Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Start Date"
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            select
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
            helperText="Use the Activate / Close buttons on the list for normal workflow"
          >
            {statusOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {statusLabel(s)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditExerciseDialog;